import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useViewCustomizeColumns } from '@monorepo/logs/src/hooks/useViewCustomizeColumns';
import { Button } from '@monorepo/shared/componentsV2/Button/Button';
import { useReorderableItems } from '@monorepo/shared/componentsV2/dragAndDrop/useReorderableItems';
import { MenuOption } from '@monorepo/shared/componentsV2/menus/Menu';
import { Search } from '@monorepo/shared/componentsV2/Search';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import * as Sentry from '@sentry/browser';
import isEqual from 'lodash.isequal';
import {
  OrderedViewColumnCustomizationsRequest,
  QueryResponse,
} from 'mapistry-shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom-v5-compat';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import {
  DisplayQueryColumn,
  getDisplayQueryColumns,
} from '../../ViewResultsTable/resultsMapper';
import {
  HiddenColumnList,
  isColumnReorderable,
  RenameColumnList,
  ReorderableColumnListItem,
  VisibleColumnList,
} from './ViewColumnListItem';

function parseOrderedViewColumnCustomizationsToRequest(
  columns: DisplayQueryColumn[],
): OrderedViewColumnCustomizationsRequest {
  return columns.map((c) => ({
    columnName: c.columnName,
    alias: ((column) => {
      const trimmedLabel = column.columnLabel.trim();
      if (trimmedLabel === '' || trimmedLabel === column.originalColumnLabel) {
        return undefined;
      }
      return trimmedLabel;
    })(c),
    isHidden: c.isHidden,
  }));
}

export enum ViewCustomizeColumnsMenuState {
  DEFAULT = 'DEFAULT',
  SHOW_COLUMNS = 'SHOW_COLUMNS',
  RENAME_COLUMNS = 'RENAME_COLUMNS',
}

const ButtonContainer = styled.div`
  width: 100%;
  margin: 0.5rem;
`;

const SaveButton = styled(Button)`
  width: 100%;

  & .MuiButton-label {
    -webkit-text-stroke: transparent;
  }
`;

const StyledArrowBackIcon = styled(ArrowBackIcon)`
  margin-right: 0.5rem;
  margin-left: -0.5rem;
`;

const StyledSearch = styled(Search)`
  width: 100%;
  color: ${({ theme }) => theme.colors.gray666};
  -webkit-text-stroke: transparent;

  && {
    border: none;
  }

  & .MuiInputBase-root {
    width: 100%;

    & input {
      color: ${({ theme }) => theme.colors.gray666};

      &::placeholder {
        color: ${({ theme }) => theme.colors.gray999};
        opacity: 1; /* Firefox */
      }
    }
  }
`;

const overrideMenuOptionStyle: React.CSSProperties = {
  height: 'auto',
  padding: 0,
  background: 'transparent',
  pointerEvents: 'all' as React.CSSProperties['pointerEvents'],
  opacity: 1,
};

const morePaddingMenuOptionStyle: React.CSSProperties = {
  padding: '20px 16px',
};

const backButtonMenuOptionStyle = (
  theme: DefaultTheme,
): React.CSSProperties => ({
  ...morePaddingMenuOptionStyle,
  background: theme.colors.grayeee,
});

export function useCurrentViewCustomizeColumnsMenuOptions({
  queryResult,
}: {
  queryResult?: QueryResponse;
}) {
  const { organizationId, projectId, viewId } = useParams();
  if (!organizationId || !projectId || !viewId) {
    throw new Error(
      "useCurrentViewCustomizeColumnsMenuOptions component was rendered in a page with url that doesn't have projectId, organizationId or viewId",
    );
  }
  const { showUserFriendlyErrorToast, success: showSuccess } = useToast();
  const [menuState, setMenuState] = useState(
    ViewCustomizeColumnsMenuState.DEFAULT,
  );
  const theme = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const [allColumns, setAllColumns] = useState<DisplayQueryColumn[]>([]);
  const [oldColumns, setOldColumns] = useState<DisplayQueryColumn[]>([]);
  const [viewColumnCustomizationUpdater] = useViewCustomizeColumns({
    config: { throwOnError: true },
  });

  // ======================================
  // Use Effect (for refreshing allColumns on query invalidation).
  // ======================================
  useEffect(() => {
    if (!queryResult) return;
    const {
      columns: queryColumns = [],
      columnSets = [],
      limitColumnSets = [],
    } = queryResult;
    const columns = getDisplayQueryColumns(
      queryColumns,
      limitColumnSets,
      columnSets,
    );
    setAllColumns(columns);
    setOldColumns(columns);
  }, [queryResult]);

  const [filter, setFilter] = useState('');
  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value),
    [setFilter],
  );
  // ======================================
  // Use Effect (for clearing searchInput on MenuState change).
  // ======================================
  useEffect(() => {
    setFilter('');
  }, [menuState]);

  // ======================================
  // Callbacks and memos.
  // ======================================
  const resetMenuState = useCallback(
    () => setMenuState(ViewCustomizeColumnsMenuState.DEFAULT),
    [],
  );
  const toggleColumnVisibility = useCallback(
    (column: DisplayQueryColumn) => {
      setAllColumns([
        ...allColumns.filter((c) => c.columnName !== column.columnName),
        {
          ...column,
          isHidden: !column.isHidden,
        },
      ]);
    },
    [allColumns],
  );
  const renameColumn = useCallback(
    (displayColumn: DisplayQueryColumn, alias: string) => {
      const columnToRename = allColumns.find(
        (c) => c.columnName === displayColumn.columnName,
      );
      if (!columnToRename) {
        const errMsg = `Couldn't rename Report column: Column not found.`;
        Sentry.captureException(errMsg);
        throw new Error(errMsg);
      }
      setAllColumns([
        ...allColumns.filter((c) => c.columnName !== columnToRename.columnName),
        {
          ...columnToRename,
          columnLabel: alias,
        },
      ]);
    },
    [allColumns],
  );
  const showAllColumns = useCallback(() => {
    setAllColumns(allColumns.map((c) => ({ ...c, isHidden: false })));
    setMenuState(ViewCustomizeColumnsMenuState.DEFAULT);
  }, [allColumns]);

  const [visibleColumns, visibleUnorderableColumns, hiddenColumns] = useMemo(
    () => [
      allColumns.filter((c) => !c.isHidden),
      allColumns.filter((c) => !c.isHidden && !isColumnReorderable(c)),
      allColumns.filter((c) => c.isHidden),
    ],
    [allColumns],
  );

  const {
    hasBeenReordered,
    orderedItems: visibleOrderableColumns,
    updateItemOrder: updateColumnOrder,
  } = useReorderableItems(
    visibleColumns?.filter(isColumnReorderable).map((column) => ({
      ...column,
      id: column.columnName,
      component: (
        <ReorderableColumnListItem
          column={column}
          disableShowHideButton={isSaving || visibleColumns.length === 1}
          toggleColumnVisibility={toggleColumnVisibility}
        />
      ),
    })),
  );

  const hasUserMadeChanges = useMemo(
    () => hasBeenReordered || !isEqual(allColumns, oldColumns),
    [allColumns, hasBeenReordered, oldColumns],
  );

  const handleSaveViewColumnCustomization = useCallback(async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);
      await viewColumnCustomizationUpdater({
        organizationId,
        projectId,
        viewId,
        orderedViewColumnCustomizations:
          parseOrderedViewColumnCustomizationsToRequest([
            ...visibleUnorderableColumns,
            ...visibleOrderableColumns,
            ...hiddenColumns,
          ]),
      });
      showSuccess(`Report column customization saved.`);
    } catch (error) {
      showUserFriendlyErrorToast(
        error,
        `Unable to save Report column customization.`,
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    viewColumnCustomizationUpdater,
    organizationId,
    projectId,
    viewId,
    visibleUnorderableColumns,
    visibleOrderableColumns,
    hiddenColumns,
    showSuccess,
    showUserFriendlyErrorToast,
  ]);

  // ======================================
  // Menu options.
  // ======================================
  const searchColumnsMenuOption = useMemo<MenuOption>(
    () => ({
      style: overrideMenuOptionStyle,
      onClick: undefined,
      onKeyDown: (event: React.KeyboardEvent<HTMLLIElement>) => {
        event.stopPropagation();
      },
      disabled: true,
      id: 'search-columns',
      children: (
        <StyledSearch
          onChange={handleSearch}
          placeholder="Search"
          value={filter}
        />
      ),
      divider: true,
    }),
    [handleSearch, filter],
  );

  const saveButtonMenuOption = useMemo<MenuOption>(
    () => ({
      style: overrideMenuOptionStyle,
      onClick: undefined,
      disabled: true,
      id: 'save-button',
      children: (
        <ButtonContainer>
          <SaveButton
            color="primary"
            onClick={handleSaveViewColumnCustomization}
            type="submit"
            variant="contained"
            disabled={!hasUserMadeChanges || isSaving}
          >
            Save
          </SaveButton>
        </ButtonContainer>
      ),
    }),
    [handleSaveViewColumnCustomization, hasUserMadeChanges, isSaving],
  );

  const backButtonMenuOption = useMemo<MenuOption>(
    () => ({
      id: 'back-button',
      onClick: () => setMenuState(ViewCustomizeColumnsMenuState.DEFAULT),
      shouldMenuStayOpenOnClick: true,
      style: backButtonMenuOptionStyle(theme),
      children: (
        <>
          <StyledArrowBackIcon />
          Back
        </>
      ),
      divider: true,
    }),
    [theme],
  );

  const defaultMenuOptions = useMemo<MenuOption[]>(
    () => [
      searchColumnsMenuOption,
      {
        id: 'visible-column-list',
        style: overrideMenuOptionStyle,
        children: (
          <VisibleColumnList
            visibleUnorderableColumns={visibleUnorderableColumns}
            visibleOrderableColumns={visibleOrderableColumns}
            isSaving={isSaving}
            updateColumnOrder={updateColumnOrder}
            toggleColumnVisibility={toggleColumnVisibility}
            filter={filter}
          />
        ),
        divider: true,
        onClick: undefined,
        disabled: true,
      },
      {
        displayName: 'Show columns',
        style: morePaddingMenuOptionStyle,
        onClick: () => setMenuState(ViewCustomizeColumnsMenuState.SHOW_COLUMNS),
        shouldMenuStayOpenOnClick: true,
        disabled: hiddenColumns.length === 0 || isSaving,
      },
      {
        displayName: 'Rename columns',
        style: morePaddingMenuOptionStyle,
        onClick: () =>
          setMenuState(ViewCustomizeColumnsMenuState.RENAME_COLUMNS),
        shouldMenuStayOpenOnClick: true,
        disabled: visibleOrderableColumns.length === 0 || isSaving,
        divider: true,
      },
      saveButtonMenuOption,
    ],
    [
      hiddenColumns.length,
      isSaving,
      saveButtonMenuOption,
      searchColumnsMenuOption,
      toggleColumnVisibility,
      updateColumnOrder,
      visibleOrderableColumns,
      visibleUnorderableColumns,
      filter,
    ],
  );

  const showColumnsMenuOptions = useMemo<MenuOption[]>(
    () => [
      backButtonMenuOption,
      searchColumnsMenuOption,
      {
        id: 'hidden-column-list',
        style: overrideMenuOptionStyle,
        children: (
          <HiddenColumnList
            hiddenColumns={hiddenColumns}
            isSaving={isSaving}
            toggleColumnVisibility={toggleColumnVisibility}
            filter={filter}
          />
        ),
        onClick: undefined,
        disabled: true,
        divider: true,
      },
      {
        displayName: 'Show all',
        style: morePaddingMenuOptionStyle,
        onClick: showAllColumns,
        shouldMenuStayOpenOnClick: true,
        disabled: isSaving || hiddenColumns.length === 0,
        divider: true,
      },
      saveButtonMenuOption,
    ],
    [
      searchColumnsMenuOption,
      backButtonMenuOption,
      hiddenColumns,
      isSaving,
      toggleColumnVisibility,
      filter,
      showAllColumns,
      saveButtonMenuOption,
    ],
  );

  const renameColumnsMenuOptions = useMemo<MenuOption[]>(
    () => [
      backButtonMenuOption,
      {
        id: 'rename-column-list',
        style: overrideMenuOptionStyle,
        children: (
          <RenameColumnList
            visibleUnorderableColumns={visibleUnorderableColumns}
            visibleOrderableColumns={visibleOrderableColumns}
            renameColumn={renameColumn}
            isSaving={isSaving}
          />
        ),
        onClick: undefined,
        disabled: true,
        divider: true,
      },
      saveButtonMenuOption,
    ],
    [
      backButtonMenuOption,
      isSaving,
      renameColumn,
      saveButtonMenuOption,
      visibleOrderableColumns,
      visibleUnorderableColumns,
    ],
  );

  const currentMenuOptions = useMemo(() => {
    switch (menuState) {
      case ViewCustomizeColumnsMenuState.SHOW_COLUMNS:
        return showColumnsMenuOptions;
      case ViewCustomizeColumnsMenuState.RENAME_COLUMNS:
        return renameColumnsMenuOptions;
      case ViewCustomizeColumnsMenuState.DEFAULT:
      default:
        return defaultMenuOptions;
    }
  }, [
    menuState,
    showColumnsMenuOptions,
    renameColumnsMenuOptions,
    defaultMenuOptions,
  ]);

  return {
    currentMenuOptions,
    isSaving,
    resetMenuState,
  };
}
